import React, { useState, useRef } from 'react';
import { BankStatementExtractResponse } from '../types';

interface BankStatementExtractResultsProps {
  results: BankStatementExtractResponse;
  onDownload: (url: string) => void;
}

export const BankStatementExtractResults: React.FC<BankStatementExtractResultsProps> = ({ results, onDownload }) => {
  const { results: extractResults, download_url, processing_time } = results;
  
  // State for collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    transactions: true,
    summary: false,
    processingTimes: false,
    detailedResponses: false
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const transactionsPerPage = 10;
  const totalTransactions = extractResults.results.length;
  const totalPages = Math.ceil(totalTransactions / transactionsPerPage);
  
  // Ref for transactions list container (the scrollable element)
  const transactionsListRef = useRef<HTMLDivElement>(null);
  
  // Calculate pagination
  const startIndex = (currentPage - 1) * transactionsPerPage;
  const endIndex = startIndex + transactionsPerPage;
  const currentTransactions = extractResults.results.slice(startIndex, endIndex);

  // Calculate total tokens from all responses
  const totalTokens = extractResults.run_responses.reduce((acc, response) => ({
    input: acc.input + response.token_count.input_token,
    output: acc.output + response.token_count.output_token
  }), { input: 0, output: 0 });

  // Helper function to render transaction fields
  const renderTransaction = (transaction: any, index: number) => {
    const globalIndex = startIndex + index;
    
    return (
      <div key={globalIndex} className="transaction-card">
        <div className="transaction-header">
          <strong>Transaction {globalIndex + 1}</strong>
        </div>
        <pre className="transaction-json">
          {JSON.stringify(transaction, null, 2)}
        </pre>
      </div>
    );
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Reset scroll position to top of transactions list
    if (transactionsListRef.current) {
      transactionsListRef.current.scrollTop = 0;
    }
  };

  const renderPaginationControls = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="btn btn-secondary pagination-btn"
      >
        ‹ Previous
      </button>
    );

    // First page
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="btn btn-secondary pagination-btn"
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`btn pagination-btn ${i === currentPage ? 'btn-primary' : 'btn-secondary'}`}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="btn btn-secondary pagination-btn"
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    pages.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="btn btn-secondary pagination-btn"
      >
        Next ›
      </button>
    );

    return (
      <div className="pagination-controls">
        <div className="pagination-info">
          Showing {startIndex + 1}-{Math.min(endIndex, totalTransactions)} of {totalTransactions} transactions
        </div>
        <div className="pagination-buttons">
          {pages}
        </div>
      </div>
    );
  };

  return (
    <div className="results">
      <div className="results-header">
        <h3>Extraction Results</h3>
        <button
          className="btn btn-success"
          onClick={() => onDownload(download_url)}
        >
          Download Results
        </button>
      </div>

      <div className="results-section">
        <div 
          className="config-header" 
          onClick={() => toggleSection('transactions')}
        >
          <button 
            type="button" 
            className={`collapse-toggle ${!expandedSections.transactions ? 'collapsed' : ''}`}
            aria-label={expandedSections.transactions ? 'Collapse transactions section' : 'Expand transactions section'}
          >
            <span>▼</span>
          </button>
          <h4>Extracted Transactions ({totalTransactions} total)</h4>
        </div>
        {expandedSections.transactions && (
          <div className="section-content">
            {totalTransactions > 0 ? (
              <>
                <div className="transactions-container">
                  <div className="transactions-list" ref={transactionsListRef}>
                    {currentTransactions.map((transaction, index) => 
                      renderTransaction(transaction, index)
                    )}
                  </div>
                </div>
                
                {renderPaginationControls()}
              </>
            ) : (
              <div className="no-transactions">
                <div className="transaction-card">
                  <div className="transaction-summary">
                    No transactions extracted. Check the detailed responses below for more information.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="results-section">
        <div 
          className="config-header" 
          onClick={() => toggleSection('summary')}
        >
          <button 
            type="button" 
            className={`collapse-toggle ${!expandedSections.summary ? 'collapsed' : ''}`}
            aria-label={expandedSections.summary ? 'Collapse summary section' : 'Expand summary section'}
          >
            <span>▼</span>
          </button>
          <h4>Extraction Summary</h4>
        </div>
        {expandedSections.summary && (
          <div className="section-content">
            <div className="results-grid">
              <div className="result-card">
                <div className="result-type">Total Transactions</div>
                <div className="result-pages">
                  {extractResults.results.length} records extracted
                </div>
              </div>
              <div className="result-card">
                <div className="result-type">Processing Runs</div>
                <div className="result-pages">
                  {extractResults.run_responses.length} LLM calls
                </div>
              </div>
            </div>
            <div className="token-usage">
              <div className="token-item">
                <span className="token-label">Total Input Tokens:</span>
                <span className="token-value">{totalTokens.input.toLocaleString()}</span>
              </div>
              <div className="token-item">
                <span className="token-label">Total Output Tokens:</span>
                <span className="token-value">{totalTokens.output.toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="results-section">
        <div 
          className="config-header" 
          onClick={() => toggleSection('processingTimes')}
        >
          <button 
            type="button" 
            className={`collapse-toggle ${!expandedSections.processingTimes ? 'collapsed' : ''}`}
            aria-label={expandedSections.processingTimes ? 'Collapse processing times section' : 'Expand processing times section'}
          >
            <span>▼</span>
          </button>
          <h4>Processing Times</h4>
        </div>
        {expandedSections.processingTimes && (
          <div className="section-content">
            <div className="processing-times">
              {Object.entries(processing_time).map(([key, time]) => (
                <div key={key} className="time-item">
                  <span className="time-label">{key}:</span>
                  <span className="time-value">{time.toFixed(2)}s</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="results-section">
        <div 
          className="config-header" 
          onClick={() => toggleSection('detailedResponses')}
        >
          <button 
            type="button" 
            className={`collapse-toggle ${!expandedSections.detailedResponses ? 'collapsed' : ''}`}
            aria-label={expandedSections.detailedResponses ? 'Collapse detailed responses section' : 'Expand detailed responses section'}
          >
            <span>▼</span>
          </button>
          <h4>Detailed Responses</h4>
        </div>
        {expandedSections.detailedResponses && (
          <div className="section-content">
            <div className="response-group">
              <h5>Processing Runs ({extractResults.run_responses.length} runs)</h5>
              {extractResults.run_responses.map((response, index) => (
                <div key={index} className="response-card">
                  <div className="response-header">
                    <div>Run {index + 1}</div>
                    <div>Time: {response.processing_time.toFixed(2)}s</div>
                    <div>Input: {response.token_count.input_token}, Output: {response.token_count.output_token}</div>
                    {response.model_info && (
                      <div>Model: {response.model_info.model_name} ({response.model_info.provider})</div>
                    )}
                  </div>
                  <div className="response-results">
                    <div className="mini-result">
                      {response.results.length} transactions extracted
                    </div>
                  </div>
                  {response.prompt && (
                    <details className="response-details">
                      <summary>Show Prompt</summary>
                      <pre className="prompt-text">{response.prompt}</pre>
                    </details>
                  )}
                  {response.messages && (
                    <details className="response-details">
                      <summary>Show OCR Results</summary>
                      <pre className="raw-text">{JSON.stringify(response.messages, null, 2)}</pre>
                    </details>
                  )}
                  {response.raw_value && (
                    <details className="response-details">
                      <summary>Show Raw Response</summary>
                      <pre className="raw-text">{response.raw_value}</pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
